import { useEffect, useMemo, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useSchool } from '@/contexts/SchoolContext';
import { useSchoolData } from '@/hooks/useSchoolData';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { ICON_MAP } from '@/constants/discipline/timelineConfig';
import { timelineService } from '@/services/disciplineService';
import { useStudentData } from '@/features/students/hooks/useStudentData';
import StudentHeader from '@/features/students/components/StudentHeader';
import StudentDisciplineTab from '@/features/students/components/StudentDisciplineTab';
import useLearnerCaseRecordAction from '@/features/students/hooks/useLearnerCaseRecordAction';

/**
 * The operational core of the canonical behaviour profile, sized for the
 * standard substrate panel: learner identity, obligations and timeline only.
 */
export default function LearnerProfileQuickView({ studentId, focusedEventId }) {
  const quickViewRef = useRef(null);
  const { schoolId } = useSchool();
  const { students = [], isLoading: studentsLoading } = useSchoolData();
  const { cachedData, loading: behaviourLoading, error } = useStudentData(studentId, schoolId);
  const student = students.find((candidate) => candidate.id === studentId);
  const learnerCaseRecordAction = useLearnerCaseRecordAction(studentId);

  const timelineEvents = useMemo(() => {
    if (!cachedData) return [];
    return timelineService.buildTimelineFromRawData(
      cachedData.mentions || [],
      cachedData.consequences || [],
      cachedData.messages || [],
    );
  }, [cachedData]);

  const eventTypeConfig = useMemo(() => {
    const config = cachedData?.eventTypeConfig || {};
    return Object.fromEntries(Object.entries(config).map(([key, value]) => [key, {
      ...value,
      icon: ICON_MAP[key] || ICON_MAP.unknown,
    }]));
  }, [cachedData]);

  const loading = studentsLoading || behaviourLoading;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (loading || error || !focusedEventId) return;

    // The Queue and quick view can render the same record simultaneously.
    // Scope the lookup to this profile so the main Queue copy never receives
    // the scroll intended for the sidebar.
    const focusedCard = Array.from(
      quickViewRef.current?.querySelectorAll('[data-timeline-event-id]') || [],
    ).find((element) => element.getAttribute('data-timeline-event-id') === focusedEventId);

    focusedCard?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [error, focusedEventId, loading, timelineEvents]);

  return (
    <div ref={quickViewRef} className="flex min-w-0 flex-col gap-4 overflow-x-hidden p-4 pb-24">
      {student && (
        <StudentHeader
          student={student}
          compact
          showBack={false}
          allowFlagEditing={false}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-10" aria-label="Loading learner behaviour">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="alert alert-warning text-sm">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span>Behaviour details could not be loaded.</span>
        </div>
      ) : !student ? (
        <div className="alert alert-warning text-sm">Learner record not found.</div>
      ) : (
        <StudentDisciplineTab
          studentId={studentId}
          allMentions={cachedData?.mentions || []}
          mentionsLoading={false}
          timelineEvents={timelineEvents}
          timelineLoading={false}
          selectedYear={currentYear}
          setSelectedYear={() => {}}
          availableYears={[]}
          eventTypeConfig={eventTypeConfig}
          categories={cachedData?.categories || []}
          getAvailableActions={() => []}
          canSeeYearFilter={false}
          consequences={cachedData?.consequences || []}
          messages={cachedData?.messages || []}
          isParentView={false}
          focusedEventId={focusedEventId}
          quickView
          canPrintCaseRecord={learnerCaseRecordAction.canPrint}
          caseRecordPreparing={learnerCaseRecordAction.isPreparing}
          onPrintCaseRecord={learnerCaseRecordAction.printCaseRecord}
        />
      )}
    </div>
  );
}
